// ============================================
// RÉFÉRENTIEL — sortie commune aux deux échelles (plan §5.4)
// ============================================
// Module PUR. Q-sort et fourchette produisent la MÊME forme de résultat → un
// seul composant de restitution pour les deux cas d'usage.

import type { DimensionReferentiel } from "./validation";
import type { StatDimension, StatFourchette } from "./agregation";
import { fiabilite, niveauxForcesQsort } from "./agregation";
import type { CibleFourchette, Echelle } from "./fourchette";
import {
  ecartDimension,
  ecartMaxDimension,
  exigence,
  POIDS_IMPORTANCE,
  POIDS_PAR_DEFAUT,
  scoreMatchingFourchette,
} from "./fourchette";
import type { VecteurQsort } from "./qsort";
import { scoreDuNiveau } from "./qsort";
import type { Correlation } from "./rapport";
import { correlationMoyenneReferents } from "./rapport";

export type CibleComparee =
  | { kind: "score"; valeur: number; dispersion: number }
  | { kind: "fourchette"; min: number; max: number; poids: number }
  /** Dimension neutralisée (pas d'attente particulière, ou non concernée par TOUS les
   *  référents) : aucune zone à comparer, mais le score du candidat reste
   *  montrable — c'est le référentiel qui n'a rien à en dire, pas le candidat
   *  qui n'a pas de score. */
  | { kind: "sans-zone" };

export type StatutDimension =
  | "dans-la-cible"
  | "sous-la-cible"
  | "au-dessus-de-la-cible"
  | "sans-zone";

export type LigneComparaison = {
  code: string;
  label: string;
  categorie?: string;
  valeurCandidat: number;
  cible: CibleComparee;
  /** Signé pour le Q-sort (candidat − entreprise), positif ou nul en fourchette. */
  ecart: number;
  statut: StatutDimension;
  /**
   * La dimension pèse-t-elle assez pour porter un point fort ou de vigilance ?
   *
   * En fourchette (PAMI) c'est l'IMPORTANCE saisie par le référent qui tranche,
   * plus la largeur de l'intervalle : les trois niveaux attendus ont des largeurs
   * quasi identiques, la largeur ne discrimine donc plus rien. En Q-sort,
   * l'exigence reste dérivée (attente tranchée et partagée).
   */
  exigenceForte: boolean;
};

export type ResultatComparaison = {
  parDimension: LigneComparaison[];
  /**
   * Fourchette : 0-100. Q-sort : moyenne des corrélations avec chaque
   * référent, dans [-1, 1] — l'indicateur global du rapport. `null` si aucune
   * dimension ne discrimine (§5.2) ou si r est indéfini.
   */
  scoreMatching: number | null;
  /** Q-sort uniquement (r, p, n) : `null` en fourchette, ou si r est indéfini.
   *  Porte le p dont la restitution tire « Pas de tendance nette ». */
  correlation: Correlation | null;
  fiabilite: "faible" | "correcte";
  pointsForts: LigneComparaison[];
  /** Sous la cible sur une exigence forte. Pour une facette HEXACO, à formuler
   *  comme un point à explorer en entretien — jamais une recommandation de rejet
   *  (§6.3). Le `categorie` est propagé pour que l'UI puisse le faire. */
  pointsVigilance: LigneComparaison[];
  /** Au-dessus de la cible : attentes du candidat que le poste ne sert pas. */
  anglesMorts: LigneComparaison[];
};

/** Tri de restitution : écart le plus marquant d'abord. */
function trierParEcart(lignes: LigneComparaison[]): LigneComparaison[] {
  return [...lignes].sort((a, b) => Math.abs(b.ecart) - Math.abs(a.ecart));
}

function assembler(
  parDimension: LigneComparaison[],
  scoreMatching: number | null,
  fiab: "faible" | "correcte",
  correlation: Correlation | null = null,
): ResultatComparaison {
  const triees = trierParEcart(parDimension);
  return {
    parDimension: triees,
    scoreMatching,
    correlation,
    fiabilite: fiab,
    pointsForts: triees.filter(
      (l) => l.statut === "dans-la-cible" && l.exigenceForte,
    ),
    pointsVigilance: triees.filter(
      (l) => l.statut === "sous-la-cible" && l.exigenceForte,
    ),
    anglesMorts: triees.filter((l) => l.statut === "au-dessus-de-la-cible"),
  };
}

/**
 * Comparaison Q-sort (fit-culturel). Les deux vecteurs sont ipsatifs (même
 * total), donc directement soustractibles — le vecteur entreprise étant celui de
 * la pyramide RECLASSÉE (`niveauxForcesQsort`), pas les moyennes continues :
 * c'est ce qui garantit que les statuts item par item et la pyramide du
 * référentiel affichée à l'écran racontent la même chose. Le score de matching,
 * lui, compare le candidat à chaque référent (`correlationMoyenneReferents`).
 *
 * « Dans la cible » se lit au CRAN EXACT : le candidat a placé l'item au niveau
 * même où la pyramide finale le place. Aucune tolérance dérivée de la dispersion
 * inter-référents n'entre ici — le référentiel EST la pyramide, et un statut qui
 * s'élargissait avec le désaccord des référents laissait l'écran dire « dans la
 * cible » là où les deux pyramides ne coïncidaient pas.
 *
 * La dispersion reste portée par `cible.dispersion`, pour la lecture : elle dit
 * si le placement du référentiel est consensuel, elle ne déplace plus le statut.
 */
export function comparerQsort(args: {
  dimensions: DimensionReferentiel[];
  vecteurCandidat: VecteurQsort;
  agregat: Record<string, StatDimension>;
  pyramide: number[];
  nbReferents: number;
  /** Niveaux forcés portés par la projection. Recalculés si absents (projections
   *  d'avant le reclassement) — même fonction, même résultat. */
  niveauxForces?: Record<string, number>;
}): ResultatComparaison {
  const nbNiveaux = args.pyramide.length;
  const niveauxForces =
    args.niveauxForces ??
    niveauxForcesQsort({
      dimensions: args.dimensions,
      agregat: args.agregat,
      pyramide: args.pyramide,
    });
  // Exigence forte = attente TRANCHÉE (score moyen dans le tiers haut ou bas de
  // l'échelle) ET partagée (dispersion sous un cran). Une dimension que les
  // référents placent au centre, ou sur laquelle ils divergent, ne discrimine pas.
  const seuilHaut = nbNiveaux - (nbNiveaux - 1) / 3;
  const seuilBas = 1 + (nbNiveaux - 1) / 3;

  const parDimension: LigneComparaison[] = [];

  for (const dimension of args.dimensions) {
    const stat = args.agregat[dimension.code];
    const valeurCandidat = args.vecteurCandidat[dimension.code];
    const niveau = niveauxForces[dimension.code];
    if (stat === undefined || valeurCandidat === undefined) continue;
    if (niveau === undefined) continue;
    const scoreEntreprise = scoreDuNiveau(niveau, nbNiveaux);

    const ecart = valeurCandidat - scoreEntreprise;
    // Cran exact : les deux vecteurs vivent sur la MÊME pyramide, un écart de
    // zéro est donc une coïncidence de niveaux, pas une approximation.
    const statut: StatutDimension =
      ecart === 0
        ? "dans-la-cible"
        : ecart < 0
          ? "sous-la-cible"
          : "au-dessus-de-la-cible";

    parDimension.push({
      code: dimension.code,
      label: dimension.label,
      categorie: dimension.categorie,
      valeurCandidat,
      cible: {
        kind: "score",
        valeur: scoreEntreprise,
        dispersion: stat.ecartType,
      },
      ecart,
      statut,
      // Attente TRANCHÉE (score du niveau dans le tiers haut ou bas de l'échelle)
      // ET partagée (dispersion sous un cran). Le score lu est celui de la
      // pyramide reclassée : « exigence forte » signifie donc exactement « item
      // placé aux niveaux extrêmes du référentiel », vérifiable à l'écran.
      exigenceForte:
        (scoreEntreprise >= seuilHaut || scoreEntreprise <= seuilBas) &&
        stat.ecartType < 1,
    });
  }

  // Moyenne des corrélations avec chaque référent (arbitrage du 30/10/2026),
  // comme l'indicateur global du rapport : le classement et le rapport lisent
  // le MÊME chiffre. Pearson, comme dans l'article d'origine (O'Reilly, Chatman
  // & Caldwell) : les extrêmes y pèsent plus que le centre. Calculée sur les
  // moyennes des référents, pas sur la pyramide reclassée, qui ne sert qu'aux
  // statuts item par item ci-dessus.
  const correlation = correlationMoyenneReferents(
    args.vecteurCandidat,
    args.agregat,
    args.pyramide,
  );

  return assembler(
    parDimension,
    correlation?.r ?? null,
    fiabilite(args.nbReferents),
    correlation,
  );
}

/**
 * Comparaison fourchette (PAMI). Une dimension SANS zone — marquée « pas
 * d'attente particulière » par le référent (historiquement « pas important »
 * ou `nonConcerne`) — est présente dans `parDimension` avec le score du candidat, mais
 * sans cible (`{ kind: "sans-zone" }`) : le référentiel n'a rien à en dire, le
 * candidat n'en a pas moins un score. Elle reste exclue du calcul (§5.2) et ne
 * peut jamais être signalée comme point fort ou de vigilance.
 */
export function comparerFourchette(args: {
  dimensions: DimensionReferentiel[];
  scoresCandidat: Record<string, number>;
  agregat: Record<string, StatFourchette>;
  echelle: Echelle;
  nbReferents: number;
  codesDesaccord?: string[];
  /** Seuil de fiabilité. 1 pour un référentiel PAMI, à référent unique. */
  seuilReferents?: number;
}): ResultatComparaison {
  const parDimension: LigneComparaison[] = [];
  const cibles: Record<string, CibleFourchette> = {};

  for (const dimension of args.dimensions) {
    const valeurCandidat = args.scoresCandidat[dimension.code];
    if (valeurCandidat === undefined) continue;

    const stat = args.agregat[dimension.code];
    if (stat === undefined) {
      parDimension.push({
        code: dimension.code,
        label: dimension.label,
        categorie: dimension.categorie,
        valeurCandidat,
        cible: { kind: "sans-zone" },
        ecart: 0,
        statut: "sans-zone",
        exigenceForte: false,
      });
      continue;
    }

    const poids = stat.poids ?? POIDS_PAR_DEFAUT;
    const cible = { min: stat.min, max: stat.max, poids };
    cibles[dimension.code] = cible;

    const ecart = ecartDimension(valeurCandidat, cible);
    const statut: StatutDimension =
      ecart === 0
        ? "dans-la-cible"
        : valeurCandidat < cible.min
          ? "sous-la-cible"
          : "au-dessus-de-la-cible";
    const discriminante = ecartMaxDimension(cible, args.echelle) > 0;

    parDimension.push({
      code: dimension.code,
      label: dimension.label,
      categorie: dimension.categorie,
      valeurCandidat,
      cible: { kind: "fourchette", min: cible.min, max: cible.max, poids },
      ecart,
      statut,
      // Le poids tranche : toujours 1 à attente unique, 0,5 pour une dimension
      // « important » d'un référentiel historique. Repli sur la largeur pour les projections
      // d'avant la pondération, où `poids` est absent et vaut 1 : sans ce repli,
      // une restitution ancienne perdrait d'un coup tous ses points forts.
      exigenceForte:
        discriminante &&
        (stat.poids === undefined
          ? exigence(cible, args.echelle) === "forte"
          : poids >= POIDS_IMPORTANCE.forte),
    });
  }

  const resultat = scoreMatchingFourchette(
    args.scoresCandidat,
    cibles,
    args.echelle,
  );

  return assembler(
    parDimension,
    resultat?.score ?? null,
    fiabilite(
      args.nbReferents,
      args.codesDesaccord?.length ?? 0,
      args.seuilReferents,
    ),
  );
}
